import { CaretDownIcon } from '@phosphor-icons/react'
import React, { useCallback } from 'react'

import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DROPDOWN_LIST_ITEM_GAP,
  DROPDOWN_LIST_ITEM_SLOT_SIZE_MAP,
} from '@/components/ui/dropdown-list'
import { Icon } from '@/components/ui/icon'
import { QuantityBadge } from '@/components/ui/quantity-badge'
import { Typography } from '@/components/ui/typography'

import * as S from './select.styled'
import {
  CheckboxState,
  SelectOptionAction,
  SelectOptionValue,
  SelectSize,
  SelectTreeOption,
} from './select.types'
import { isTreeOptionCheckboxDisabled } from './select.utils'
import { SelectOptionButton } from './select-option-button'
import { SelectTruncationTooltip } from './select-truncation-tooltip'

interface SelectOptionItemProps<V extends SelectOptionValue> {
  depth: number
  expanded: boolean
  hasAnyExpandable: boolean
  hasChildren: boolean
  // Note: the 'menu' dropdown variant - see SelectDropdownVariant.
  isMenu?: boolean
  isMulti: boolean
  onOptionClick: (value: V, disabled?: boolean) => void
  onToggleExpanded: (key: V) => void
  onOptionAction?: (action: SelectOptionAction) => void
  option: SelectTreeOption<V>
  selected: boolean
  size: SelectSize
  style: React.CSSProperties
  /** Only set in tree-checkbox mode - this option's tri-state, derived from its descendants. */
  treeCheckboxState?: CheckboxState
  onTreeCheckboxToggle?: (option: SelectTreeOption<V>) => void
}

export const SelectOptionItem = <V extends SelectOptionValue>({
  depth,
  expanded,
  hasAnyExpandable,
  hasChildren,
  isMenu,
  isMulti,
  onOptionClick,
  onToggleExpanded,
  onOptionAction,
  option,
  selected,
  size,
  style,
  treeCheckboxState,
  onTreeCheckboxToggle,
}: SelectOptionItemProps<V>) => {
  const isTreeCheckbox = treeCheckboxState !== undefined
  const isCheckboxDisabled = isTreeCheckbox && isTreeOptionCheckboxDisabled(option)

  const handleRowClick = useCallback(() => {
    if (hasChildren) {
      onToggleExpanded(option.value)
    } else if (isTreeCheckbox) {
      if (!isCheckboxDisabled) onTreeCheckboxToggle?.(option)
    } else {
      onOptionClick(option.value, option.disabled)
    }
  }, [
    hasChildren,
    onToggleExpanded,
    isTreeCheckbox,
    isCheckboxDisabled,
    onTreeCheckboxToggle,
    onOptionClick,
    option,
  ])

  const handleSelectButtonClick = useCallback(() => {
    onOptionClick(option.value, option.disabled)
  }, [onOptionClick, option.value, option.disabled])

  const handleTreeCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      // Note: EL-15446 - the checkbox is a <label> around a hidden <input>, so a click on its box
      // makes the label forward a second click to the input, which bubbles back here and toggles
      // the option straight back. Cancel that forwarding; this handler does the toggling.
      e.preventDefault()
      e.stopPropagation()
      if (!isCheckboxDisabled) onTreeCheckboxToggle?.(option)
    },
    [isCheckboxDisabled, onTreeCheckboxToggle, option],
  )

  // Note: the 'menu' variant's rows keep the small 32px height but use the 20px checkbox.
  const checkboxSize = isMenu ? 'medium' : size
  const caretSize = DROPDOWN_LIST_ITEM_SLOT_SIZE_MAP[size]
  // Note: a leaf next to expandable siblings reserves the caret's slot plus the row's own gap, so
  // its checkbox lines up with theirs (the old hardcoded 0.8rem assumed an 8px gap - rows use 4px).
  const caretOffset =
    hasAnyExpandable && !hasChildren ? `calc(${caretSize} + ${DROPDOWN_LIST_ITEM_GAP})` : '0px'

  return (
    <S.VirtualItem role="option" aria-selected={selected} style={style}>
      <S.Option
        // Note: the design's "Active" row is the chosen option of a single-select - multi and
        // tree-checkbox rows already show their state through the checkbox.
        $isActive={selected && !isMulti && !isTreeCheckbox}
        $isMenu={!!isMenu}
        $size={size}
        $isDisabled={!!option.disabled}
        $depth={depth}
        $caretOffset={caretOffset}
        onClick={handleRowClick}
      >
        {hasChildren && (
          <S.ExpandToggle $isExpanded={expanded}>
            <Icon icon={CaretDownIcon} size={DROPDOWN_LIST_ITEM_SLOT_SIZE_MAP[size]} />
          </S.ExpandToggle>
        )}
        {option.icon && !option.badgeColor && (
          <Icon icon={option.icon} size={DROPDOWN_LIST_ITEM_SLOT_SIZE_MAP[size]} />
        )}
        {isMulti && (
          <S.CheckboxWrapper>
            <Checkbox
              readOnly
              checked={selected}
              disabled={!!option.disabled}
              size={checkboxSize}
            />
          </S.CheckboxWrapper>
        )}
        {isTreeCheckbox && (
          <S.CheckboxWrapper $interactive onClick={handleTreeCheckboxClick}>
            <Checkbox
              readOnly
              checked={treeCheckboxState === 'checked'}
              disabled={isCheckboxDisabled}
              indeterminate={treeCheckboxState === 'indeterminate'}
              size={checkboxSize}
            />
          </S.CheckboxWrapper>
        )}
        {option.badgeColor ? (
          <Badge
            color={option.badgeColor}
            icon={option.icon}
            size="small"
            variant={option.badgeVariant ?? 'light'}
          >
            {option.label}
          </Badge>
        ) : (
          <SelectTruncationTooltip label={option.label}>
            {(truncationProps) => (
              <Typography {...truncationProps} size="unset" breakLines={1}>
                {option.label}
              </Typography>
            )}
          </SelectTruncationTooltip>
        )}
        {option.status && (
          <S.TriggerStatusBadge size="tiny" color={option.statusColor ?? 'neutral'} variant="light">
            {option.status}
          </S.TriggerStatusBadge>
        )}
        {option.action && (
          <S.OptionAction
            use="ghost"
            size="small"
            onClick={(event) => {
              event.stopPropagation()
              if (!option.action) return
              if (onOptionAction) onOptionAction(option.action)
              else option.action.onClick()
            }}
          >
            {option.action.icon && <Icon icon={option.action.icon} size="1.4rem" />}
            {option.action.label}
          </S.OptionAction>
        )}
        {option.count !== undefined && (
          <S.OptionCountBadge>
            <QuantityBadge quantity={option.count} />
          </S.OptionCountBadge>
        )}
        {/*NOTE this is meant only for folder/nested select - superseded by the
            checkbox in tree-checkbox mode, where it would be a redundant, confusing
            second way to select a parent.*/}
        {hasChildren && !option.disabled && !option.hideMoveButton && !isTreeCheckbox && (
          <SelectOptionButton onClick={handleSelectButtonClick} />
        )}
      </S.Option>
    </S.VirtualItem>
  )
}
